import React, { type MouseEvent, useState } from 'react'

import { Box, Icon, StandardTile, StandardTileContents, StandardTileName, StandardTileImage, StandardTilePrice, Button, Spinner } from '@vp/swan'
import { PurcsPreview, PreviewModes } from '@vp/react-product-preview'
import { PageId, PriceDisplay, useSetPriceConfigs } from '@vp/price-platform'
import { useLocalizations } from '@vp/ubik-localizations-provider'
import { useLogger } from '@vp/ubik-context'

import { useAppState } from '../root/contexts/AppContext'
import { useTenant } from '../root/contexts/TenantContext'
import type { RelatedProduct } from '../../types/RelatedProduct'
import type { ColorOptionProduct } from '../../types/ColorOptionProduct'
import type { WorkId } from '../../types/WorkId'
import { ColorName } from '../quantity/colorOption/ColorName'
import { createWorkFromColorOption, getColorKey } from '../quantity/colorOption/colorOptionHelpers'
import { createWorkFromRelatedProduct } from '../quantity/relatedProduct/relatedProductsHelper'
import { getEditUrlPathFromUrl } from '../../utils/WorkHelpers'
import { useAuthorization } from '../root/contexts/AuthorizationProvider'
import { PURCS_ORIGIN_URL, REQUESTOR } from '../../utils/constants'

export enum Mode {
  RELATED_PRODUCT,
  COLOR_OPTION,
}

type ProductTileProps<T extends RelatedProduct | ColorOptionProduct = RelatedProduct | ColorOptionProduct> = {
  mode: Mode
  product: T
  position?: number
  onAddProduct: (workId: WorkId, product: T, position: number) => void
}

const ProductTileComponent = <T extends RelatedProduct | ColorOptionProduct>({ mode, product, position = 0, onAddProduct }: ProductTileProps<T>) => {
  const { t } = useLocalizations()
  const { primaryProject, isCareAgent, culture } = useAppState()
  const { authorizationHeader } = useAuthorization()
  const { tenant, cartTenant } = useTenant()
  const logger = useLogger()

  const [addingProduct, setAddingProduct] = useState<boolean>(false)

  useSetPriceConfigs({
    locale: culture,
    tenant,
    pageId: PageId.QuantitySelectionPageTiles,
    developmentMode: process.env['NODE_ENV'] === 'development',
  })

  const addProduct = async (): Promise<void> => {
    if (!authorizationHeader) {
      throw new Error('authorizationHeader undefined')
    }

    let workId: WorkId | undefined = product?.workId

    if (!workId) {
      if (!primaryProject) {
        throw new Error('there is no primary project')
      }
      /* we need to figure out the editUrl.
         it should be safe to assume the same
         studio would be used based on the primary
         work for now.
       */
      const editUrlPath = getEditUrlPathFromUrl(primaryProject.work.design.editUrl)

      if (mode === Mode.RELATED_PRODUCT) {
        workId = await createWorkFromRelatedProduct(
          cartTenant,
          tenant,
          culture,
          primaryProject.work.ownerId,
          isCareAgent,
          editUrlPath,
          product as RelatedProduct,
          primaryProject.pageMode,
          authorizationHeader,
          logger,
        )
      } else if (mode === Mode.COLOR_OPTION) {
        workId = await createWorkFromColorOption(
          cartTenant,
          tenant,
          culture,
          primaryProject.work.ownerId,
          REQUESTOR,
          isCareAgent,
          editUrlPath,
          primaryProject.pageMode,
          primaryProject,
          (product as ColorOptionProduct).hexColorValue,
          getColorKey(primaryProject.lowestPriceAttributes),
          authorizationHeader,
          logger,
          (product as ColorOptionProduct).workId,
        )
      } else {
        throw new Error('Cannot generate workId for product')
      }
    }

    onAddProduct(workId!, product, position) // workId should be defined here
  }

  const handleAddClick = async (e: MouseEvent) => {
    e.preventDefault()
    setAddingProduct(true)
    try {
      await addProduct()
    } catch (err) {
      logger.warn('Failed to add new work', {
        error: err,
        productKey: product.productKey,
      })
      setAddingProduct(false)
    }
  }

  return (
    <Box className="qsp-carousel-product">
      <StandardTile layout="horizontal" imageWidth="full-bleed" mb={0} px={6} py={6}>
        <StandardTileImage>
          <PurcsPreview
            tenantId={cartTenant.toLowerCase()}
            requestor={REQUESTOR}
            coreProductKey={product.productKey}
            productVersion={product.productVersion}
            optionSelections={product.selectedOptions}
            previewInstructionsUri={product?.renderingInstructionsUrl}
            previewMode={PreviewModes.FirstPageOnly}
            locale={culture}
            purcsOrigin={PURCS_ORIGIN_URL}
            width={1000} // default value
            productName={product.productName}
          />
        </StandardTileImage>
        <StandardTileContents>
          <StandardTileName className="qsp-carousel-product-name" overflow="hidden">
            {product.productName}
          </StandardTileName>
          {mode === Mode.COLOR_OPTION && (
            <ColorName
              mt={4}
              className="qsp-carousel-product-name qsp-carousel-product-name-with-color"
              overflow="hidden"
              colorOptionProduct={product as ColorOptionProduct}
            />
          )}
          <Box mb="to-actions">
            <StandardTilePrice fontSize="small">
              <PriceDisplay
                productKey={product.productKey}
                version={product.productVersion}
                selections={product.selectedOptions}
                isQuantitySliderEnabled={true}
                hideSetupFee={true}
              />
            </StandardTilePrice>
          </Box>
          <Button skin="secondary" width="standard" iconPosition="left" onClick={handleAddClick} disabled={addingProduct} mt={0}>
            {addingProduct ? (
              <Box marginRight={3}>
                <Spinner accessibleText={t('loading-label')} />
              </Box>
            ) : (
              <Icon iconType="add" aria-hidden="true" />
            )}
            {t('related-product.add')}
          </Button>
        </StandardTileContents>
      </StandardTile>
    </Box>
  )
}

// Memoize with custom comparison that only checks product identity
export const ProductTile = React.memo(
  ProductTileComponent,
  <T extends RelatedProduct | ColorOptionProduct>(prevProps: ProductTileProps<T>, nextProps: ProductTileProps<T>) => {
    // Return true if props are equal (skip re-render), false if different (re-render)
    const isSameProduct = prevProps.product.productKey === nextProps.product.productKey
    const isSameMode = prevProps.mode === nextProps.mode
    const isSameCallback = prevProps.onAddProduct === nextProps.onAddProduct

    // For color options, also check hex color value
    const isSameColor =
      prevProps.mode === Mode.COLOR_OPTION
        ? (prevProps.product as ColorOptionProduct).hexColorValue === (nextProps.product as ColorOptionProduct).hexColorValue
        : true

    return isSameProduct && isSameMode && isSameColor && isSameCallback
  },
) as typeof ProductTileComponent
